'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { ArrowRight, Menu, X } from 'lucide-react';

const navigation = [
  { name: 'Accueil', href: '/' },
  { name: 'À propos', href: '/about' },
  { name: 'Activités', href: '/activities' },
  { name: 'Événements', href: '/events' },
  { name: 'Actualités', href: '/blog' },
  { name: 'Galerie', href: '/gallery' },
  { name: 'Témoignages', href: '/testimonials' },
  { name: 'FAQ', href: '/faq' },
  { name: 'Contact', href: '/contact' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <nav className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8" aria-label="Navigation principale">
        {/* Logo */}
        <div className="flex xl:flex-1">
          <Link href="/" className="-m-1.5 p-1.5 flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Image
              src="/images/logo.jpg"
              alt="Logo C2E"
              width={50}
              height={50}
              className="h-12 w-auto rounded-full"
            />
            <div suppressHydrationWarning>
              <span className="text-xl font-bold text-c2e-blue">C2E</span>
              <p className="text-xs text-c2e-gray hidden sm:block">Club des Élèves et Étudiants</p>
            </div>
          </Link>
        </div>

        {/* Menu hamburger mobile */}
        <div className="flex xl:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-c2e-blue"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <span className="sr-only">Ouvrir le menu</span>
            {mobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Menu desktop */}
        <div className="hidden xl:flex xl:gap-x-7">
          {navigation.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.name}
                href={item.href}
                className={`text-sm font-semibold leading-6 transition-colors relative ${
                  isActive 
                    ? 'text-c2e-blue' 
                    : 'text-gray-700 hover:text-c2e-blue'
                }`}
              >
                {item.name}
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-c2e-blue rounded-full" />
                )}
              </Link>
            );
          })}
        </div>

        {/* CTA Rejoindre */}
        <div className="hidden xl:flex xl:flex-1 xl:justify-end">
          <Link
            href="/join"
            className="rounded-full bg-c2e-red px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-c2e-red-light transition-colors"
          >
            Rejoindre le C2E
          </Link>
        </div>
      </nav>

      {/* Menu mobile */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-white border-t border-gray-200">
          <div className="space-y-1 px-4 pb-3 pt-2">
            {navigation.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`block rounded-lg px-3 py-2 text-base font-semibold leading-7 transition-colors ${
                    isActive
                      ? 'bg-c2e-blue text-white'
                      : 'text-gray-700 hover:bg-c2e-blue-lightest hover:text-c2e-blue'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              );
            })}
            <Link
              href="/join"
              className="block rounded-lg px-3 py-2.5 text-base font-semibold text-white bg-c2e-red hover:bg-c2e-red-light text-center mt-4"
              onClick={() => setMobileMenuOpen(false)}
            >
              Rejoindre le C2E
            </Link>
          </div>
        </div>
      )}

      {/* CTA fixe mobile */}
      <div className="xl:hidden fixed bottom-4 right-4 z-50">
        <Link
          href="/join"
          className="flex items-center gap-2 rounded-full bg-c2e-red px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-c2e-red-light transition-all hover:scale-105"
        >
          Rejoindre
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </header>
  );
}
